import { useState } from 'react';
import { updateProfile, deleteProfile } from '../api/pocketbase';
import { formatDateTime, getDateTimeInputValue, toPocketBaseDateTime } from '../utils/dateFields';

function ProfileCard({ profile, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    email: profile.email,
    app_password: profile.app_password,
    cnn: profile.cnn,
    date_reserver: profile.date_reserver ? profile.date_reserver.split(' ')[0] : '',
    date_taken: getDateTimeInputValue(profile.date_taken),
    id_pc: (!profile.id_pc || profile.id_pc === '-' || profile.id_pc.toLowerCase() === 'no dispo') ? '' : profile.id_pc,
    phone_number: profile.phone_number === '-' ? '' : profile.phone_number,
  });
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [appointmentInfo, setAppointmentInfo] = useState(null);

  const getTodayStr = () => {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().split('T')[0];
  };
  const todayStr = getTodayStr();
  const dateStr = profile.date_reserver ? profile.date_reserver.split(' ')[0].split('T')[0] : null;
  const idPcEmpty = !profile.id_pc || profile.id_pc.trim() === '' || profile.id_pc === '-' || profile.id_pc.toLowerCase() === 'no dispo';
  const isFutureDate = (dateValue) => {
    const normalized = dateValue ? dateValue.split(' ')[0].split('T')[0] : '';
    return normalized >= todayStr;
  };

  const isReserved = dateStr && dateStr >= todayStr;
  const isAvailable = dateStr && dateStr < todayStr && idPcEmpty;
  const isActive = dateStr && dateStr < todayStr && !idPcEmpty;

  const borderColor = isReserved
    ? 'border-amber-500/20 bg-amber-500/[0.03]'
    : isAvailable
    ? 'border-emerald-500/20 bg-emerald-500/[0.03]'
    : isActive
    ? 'border-purple-500/20 bg-purple-500/[0.03]'
    : 'border-white/[0.06] bg-white/[0.02]';

  const hoverBorder = editing
    ? ''
    : isReserved
    ? 'hover:border-amber-500/30 hover:bg-amber-500/[0.05]'
    : isAvailable
    ? 'hover:border-emerald-500/30 hover:bg-emerald-500/[0.05]'
    : isActive
    ? 'hover:border-purple-500/30 hover:bg-purple-500/[0.05]'
    : 'hover:border-white/[0.1] hover:bg-white/[0.04]';

  const avatarBg = isReserved
    ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
    : isAvailable
    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
    : isActive
    ? 'bg-purple-500/10 border-purple-500/20 text-purple-400'
    : 'bg-white/[0.04] border-white/[0.08] text-zinc-400';

  const badge = isReserved ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      Reserved
    </span>
  ) : isAvailable ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
      Disponible
    </span>
  ) : isActive ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-purple-500/10 text-purple-400 border border-purple-500/20">
      <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
      Active
    </span>
  ) : null;

  const handleDelete = async () => {
    try {
      await deleteProfile(profile.id);
      onUpdated();
    } catch (_) {}
  };

  const handleDownload = async () => {
    if (!profile.app_password || profile.app_password === '-') {
      alert('No app password set for this profile');
      return;
    }
    setDownloading(true);
    try {
      const response = await fetch('/api/download-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email, app_password: profile.app_password }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Download failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="(.+)"/);
      a.download = match ? match[1] : 'appointment.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
    setDownloading(false);
  };

  const handleCheck = async () => {
    if (!profile.app_password || profile.app_password === '-') {
      alert('No app password set for this profile');
      return;
    }
    setChecking(true);
    setAppointmentInfo(null);
    try {
      const response = await fetch('/api/check-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email, app_password: profile.app_password }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Check failed');
      }
      const data = await response.json();
      setAppointmentInfo({
        appointmentDate: data.appointmentDate,
        dateTaken: data.dateTaken,
      });

      const updates = {};
      if (data.appointmentDate && data.appointmentDate !== 'Not found in PDF') {
        const appointmentDate = data.appointmentDate.split(' ')[0];
        updates.date_reserver = appointmentDate;
        if (isFutureDate(appointmentDate)) updates.id_pc = '-';
      }
      if (data.dateTaken && data.dateTaken !== 'Not found in PDF') {
        updates.date_taken = toPocketBaseDateTime(data.dateTaken);
      }

      if (Object.keys(updates).length > 0) {
        await updateProfile(profile.id, updates);
        setTimeout(() => onUpdated(), 5000);
      }
    } catch (err) {
      setAppointmentInfo('Error: ' + err.message);
    }
    setChecking(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile(profile.id, {
        email: form.email.trim(),
        app_password: form.app_password.trim(),
        cnn: form.cnn.trim(),
        date_reserver: form.date_reserver,
        date_taken: toPocketBaseDateTime(form.date_taken) || null,
        id_pc: isFutureDate(form.date_reserver) ? '-' : form.id_pc.trim() || '-',
        phone_number: form.phone_number.trim() || '-',
      });
      setEditing(false);
      onUpdated();
    } catch (_) {}
    setSaving(false);
  };

  const handleCancel = () => {
    setForm({
      email: profile.email,
      app_password: profile.app_password,
      cnn: profile.cnn,
      date_reserver: profile.date_reserver ? profile.date_reserver.split(' ')[0] : '',
      date_taken: getDateTimeInputValue(profile.date_taken),
      id_pc: (!profile.id_pc || profile.id_pc === '-' || profile.id_pc.toLowerCase() === 'no dispo') ? '' : profile.id_pc,
      phone_number: profile.phone_number === '-' ? '' : profile.phone_number,
    });
    setEditing(false);
  };

  const inputCls = "w-full px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.04] text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-white/20 transition-all duration-150";

  if (editing) {
    return (
      <div className={`border border-blue-500/20 bg-blue-500/[0.03] rounded-lg transition-all duration-150 overflow-hidden`}>
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Email</label>
              <input name="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">App Password</label>
              <input name="app_password" value={form.app_password} onChange={(e) => setForm(p => ({ ...p, app_password: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">CNN</label>
              <input name="cnn" value={form.cnn} onChange={(e) => setForm(p => ({ ...p, cnn: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Date Reserver</label>
              <input name="date_reserver" type="date" value={form.date_reserver} onChange={(e) => setForm(p => ({ ...p, date_reserver: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Date Taken</label>
              <input name="date_taken" type="datetime-local" value={form.date_taken} onChange={(e) => setForm(p => ({ ...p, date_taken: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">ID PC</label>
              <input name="id_pc" value={form.id_pc} onChange={(e) => setForm(p => ({ ...p, id_pc: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">Phone Number</label>
              <input name="phone_number" value={form.phone_number} onChange={(e) => setForm(p => ({ ...p, phone_number: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-white/[0.06]">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-white text-[#09090b] text-xs font-medium hover:bg-zinc-200 active:scale-[0.98] transition-all disabled:opacity-40"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 rounded-lg bg-white/[0.05] text-zinc-400 text-xs font-medium hover:bg-white/[0.08] hover:text-zinc-300 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`group border rounded-lg ${borderColor} ${hoverBorder} transition-all duration-150 overflow-hidden`}>
      <div className="px-5 py-4 flex items-center gap-4">
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center text-xs font-semibold flex-shrink-0 uppercase ${avatarBg}`}>
          {(profile.email || '?')[0]}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-200 truncate">{profile.email}</span>
            {badge}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
            {profile.phone_number && profile.phone_number !== '-' && (
              <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                {profile.phone_number}
              </span>
            )}
            {profile.cnn && profile.cnn !== '-' && (
              <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                {profile.cnn}
              </span>
            )}
            {profile.id_pc && profile.id_pc !== '-' && profile.id_pc.toLowerCase() !== 'no dispo' && (
              <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                PC: {profile.id_pc}
              </span>
            )}
            {profile.date_reserver && (
              <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                {profile.date_reserver}
              </span>
            )}
            {profile.date_taken && (
              <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                Taken: {formatDateTime(profile.date_taken)}
              </span>
            )}
          </div>
          {appointmentInfo && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
              {typeof appointmentInfo === 'string' ? (
                appointmentInfo
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span><span className="font-medium">Appointment:</span> {appointmentInfo.appointmentDate}</span>
                  <span><span className="font-medium">Date Taken:</span> {appointmentInfo.dateTaken}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="p-2 rounded-lg text-zinc-600 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all duration-150 disabled:opacity-40"
            title="Download PDF"
          >
            {downloading ? (
              <div className="w-[15px] h-[15px] border-2 border-zinc-700 border-t-emerald-400 rounded-full animate-spin" />
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
            )}
          </button>
          <button
            onClick={handleCheck}
            disabled={checking}
            className="p-2 rounded-lg text-zinc-600 hover:text-blue-400 hover:bg-blue-500/10 transition-all duration-150 disabled:opacity-40"
            title="Check Appointment"
          >
            {checking ? (
              <div className="w-[15px] h-[15px] border-2 border-zinc-700 border-t-blue-400 rounded-full animate-spin" />
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
            )}
          </button>
          <button
            onClick={() => setEditing(true)}
            className="p-2 rounded-lg text-zinc-600 hover:text-blue-400 hover:bg-blue-500/10 transition-all duration-150"
            title="Edit"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button
            onClick={handleDelete}
            className="p-2 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all duration-150"
            title="Delete"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
              <line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileCard;
